import type { AgingBuckets } from '../components/CustomerAgingBreakdown';

type CustomerSaleAgingInput = {
  createdAt: string;
  pendingAmount: number;
};

const DAY_MS = 1000 * 60 * 60 * 24;

function bucketForDays(days: number): keyof AgingBuckets {
  if (days <= 30) return 'bucket_0_30';
  if (days <= 60) return 'bucket_31_60';
  if (days <= 90) return 'bucket_61_90';
  return 'bucket_90_plus';
}

/**
 * Calcula aging buckets a partir de ventas pendientes + updatedAt para deuda manual.
 *
 * - Cada venta pendiente se bucketea por días desde su `createdAt`.
 * - El remanente (balance - sum(pendingAmounts)) se bucketea por días desde `updatedAt`.
 * - Si no hay `updatedAt` para el remanente, cae en bucket_0_30.
 * - Retorna null si balance <= 0.
 */
export function computeAgingBuckets(
  balance: number,
  customerSales: ReadonlyArray<CustomerSaleAgingInput>,
  updatedAt: string | null,
  now: number = Date.now()
): AgingBuckets | null {
  if (balance <= 0) return null;

  const buckets: AgingBuckets = {
    bucket_0_30: 0,
    bucket_31_60: 0,
    bucket_61_90: 0,
    bucket_90_plus: 0,
  };

  let allocatedFromSales = 0;

  for (const sale of customerSales) {
    const pending = Math.max(0, sale.pendingAmount);
    if (pending <= 0) continue;
    const createdMs = new Date(sale.createdAt).getTime();
    if (isNaN(createdMs)) continue;
    const days = Math.floor((now - createdMs) / DAY_MS);
    buckets[bucketForDays(days)] += pending;
    allocatedFromSales += pending;
  }

  const manualDebt = Math.max(0, balance - allocatedFromSales);
  if (manualDebt > 0) {
    let manualDays = 0;
    if (updatedAt) {
      const updatedMs = new Date(updatedAt).getTime();
      if (!isNaN(updatedMs)) {
        manualDays = Math.floor((now - updatedMs) / DAY_MS);
      }
    }
    buckets[bucketForDays(manualDays)] += manualDebt;
  }

  return buckets;
}
